/**
 * WARNING: This module must not indirectly import WebContainer.
 *
 * This means that imports should not be added lightly.
 * The reason is that this is used by our `clientRequest`
 * wrapper.
 */

import { atom, map, type ReadableAtom } from 'nanostores';
import { ContextSizeTracker, type ContextSizeEvent } from './context-size-tracker';
import { clientFetch } from '~/lib/.client/fetch';
import { DEFAULT_TITLE } from '~/lib/constants';
import type { ForkedProjectData, ProjectData, ProjectVisibility } from '~/lib/projects/entities';
import type { OrganizationData, ProjectIntegrations } from '~/types/organization';
import { logger } from '~/utils/logger';
import { type NormalizedProjectPermission, normalizePermissions } from '~/utils/permissions';

type EventTypes = 'fork' | 'conflict' | 'project-too-big';

type EventListeners = {
  fork: () => void;
  conflict: (lastEditor: string, editedAt: string) => void;
  'project-too-big': () => void;
};

class ProjectStore {
  #projectId = atom<string | undefined>();
  #slug = atom<string | undefined>();
  #editedAt = atom<string | undefined>();
  #shouldFork = atom<boolean>(false);

  #listeners = new Map<EventTypes, Array<EventListeners[EventTypes]>>();

  duplicateModalOpen = atom<boolean>(false);

  title: string | undefined;

  visibility = atom<ProjectVisibility | undefined>();
  permissions = map<NormalizedProjectPermission>();
  isForking = atom<boolean>(false);
  organization = atom<OrganizationData | null>(null);
  integrations = atom<ProjectIntegrations | null>(null);

  #contextSizeTracker = new ContextSizeTracker();

  get projectId(): ReadableAtom<string | undefined> {
    return this.#projectId;
  }

  setProjectId(projectId: string) {
    this.#projectId.set(projectId);
  }

  getProjectId() {
    return this.#projectId.get();
  }

  setSlug(slug: string) {
    this.#slug.set(slug);
  }

  getSlug() {
    return this.#slug.get();
  }

  async fetchTitle(prompt: string) {
    let title = DEFAULT_TITLE;

    try {
      const response = await clientFetch('/api/title', {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      });

      const { title: generatedTitle } = await response.json<{ title: string }>();

      if (generatedTitle) {
        title = generatedTitle;
      }
    } catch (error) {
      logger.error(error);
    }

    this.title = title;
  }

  getSlugStore(): ReadableAtom<string | undefined> {
    return this.#slug;
  }

  refreshContextSize(event: ContextSizeEvent) {
    return this.#contextSizeTracker.refresh(event);
  }

  waitForSlug() {
    return new Promise<string>((resolve) => {
      this.#slug.subscribe((slug) => {
        if (slug) {
          resolve(slug);
        }
      });
    });
  }

  setEditedAt(editedAt: string) {
    this.#editedAt.set(editedAt);
  }

  getEditedAt() {
    return this.#editedAt.get();
  }

  setShouldFork(input: boolean | ForkedProjectData | ProjectData, activeOrganization: OrganizationData | null) {
    if (typeof input === 'boolean') {
      this.#shouldFork.set(input);

      return;
    }

    // org tokens can only be used for the org projects so if the project doesn't belong to the org we will fork it
    if (activeOrganization && input.organization?.id !== activeOrganization.id) {
      this.#shouldFork.set(true);

      return;
    }

    const { canManage, currentUserIsOwner } = input.access;

    this.#shouldFork.set(!(canManage || currentUserIsOwner) || !!input.frozen);
  }

  shouldFork() {
    return this.#shouldFork.get();
  }

  registerListener<T extends EventTypes>(type: T, listener: EventListeners[T]) {
    const listeners = this.#listeners.get(type) || [];
    listeners.push(listener);
    this.#listeners.set(type, listeners);

    return () => {
      const listeners = this.#listeners.get(type) || [];

      listeners.filter((oldListener) => oldListener !== listener);

      this.#listeners.set(type, listeners);
    };
  }

  emit<T extends EventTypes>(type: T, ...args: Parameters<EventListeners[T]>) {
    this.#listeners.get(type)?.forEach((l) => (l as any)(...args));
  }

  updateProject(project: ForkedProjectData | ProjectData, activeOrganization: OrganizationData | null) {
    this.setProjectId(project.id);
    this.setEditedAt(project.editedAt);
    this.setSlug(project.slug);
    this.setShouldFork(project, activeOrganization);
    this.permissions.set(normalizePermissions(project.access));
    this.visibility.set(project.visibility);
    this.organization.set(project.organization);
    this.integrations.set(project.integrations ?? null);
  }
}

export const projectStore = new ProjectStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { projectStore });
}
